import { List, Stack, Typography } from '@mui/material'
import { type PropsWithChildren } from 'react'

import AzureLogo from 'assets/logos/azure-white.svg?component'
import { AuthenticationStep } from 'components/common/mapped-entities/AuthenticationStep'
import { getAzureMetadataAuthenticationUrlFromResponse } from 'components/common/mapped-entities/azure/authentication/azure-integration-utils'
import { MUIExternalLink } from 'components/common/MUIExternalLink'
import { MUIExternalLinkButton } from 'components/common/MUIExternalLinkButton'
import Icon from 'components/Icon'
import { muiTheme } from 'mui-theme'
import { isPrivateEdition } from 'utils/app-config'

function AzureDeploymentButton({
  isReadOnly,
  templateUrl
}: {
  isReadOnly: boolean
  templateUrl: string
}) {
  return (
    <MUIExternalLinkButton
      disabled={isReadOnly}
      href={getAzureMetadataAuthenticationUrlFromResponse(templateUrl)}
      startIcon={
        <AzureLogo
          aria-label="Azure Icon"
          css={{
            height: muiTheme.spacing(3),
            width: muiTheme.spacing(3)
          }}
        />
      }
    >
      Deploy to Azure
    </MUIExternalLinkButton>
  )
}

/**
 * children will likely be a form context wrapping a
 * subscription field, ie:
 * `<TextField placeholder="Subscription ID" />` in a form controller
 */
export function AzureAuthenticationSection({
  children,
  templateUrl,
  isReadOnly = false
}: PropsWithChildren<{
  templateUrl: string | null
  isReadOnly?: boolean
}>) {
  // No ARM template deploy step in Private Edition; show a simplified
  // verify-access and Subscription ID guide instead.
  if (isPrivateEdition()) {
    return (
      <Stack sx={{ rowGap: 3 }}>
        <List disablePadding>
          <AuthenticationStep badgeNumber={1} title="Verify Gremlin Access">
            <Typography>
              Ensure your Azure Subscription has been added as an authorized
              scope on the Gremlin service principal. If you're unsure, contact
              your administrator.
            </Typography>
          </AuthenticationStep>
          <AuthenticationStep badgeNumber={2} title="Paste Subscription ID">
            <Typography>
              Copy the Subscription ID and enter it in the field below.
            </Typography>
            {children}
          </AuthenticationStep>
        </List>
      </Stack>
    )
  }

  return (
    <Stack sx={{ rowGap: 3 }}>
      <List disablePadding>
        <AuthenticationStep
          badgeNumber={1}
          title="Do you have the correct role and permissions?"
        >
          <Typography>
            This deployment must be done by an account in the customer's tenant
            who has a role with the{' '}
            <code>Microsoft.Authorization/roleAssignments/write</code>,{' '}
            <code>Microsoft.Authorization/roleAssignments/delete</code>, and{' '}
            <code>Microsoft.Authorization/roleAssignments/read</code>{' '}
            permissions, such as{' '}
            <MUIExternalLink
              href="https://learn.microsoft.com/en-us/azure/role-based-access-control/built-in-roles#owner"
              sx={{
                alignItems: 'baseline',
                columnGap: 0.5,
                display: 'inline-flex',
                flexDirection: 'row'
              }}
            >
              Owner
              <Icon icon="arrowUpRightFromSquare" />
            </MUIExternalLink>
            . The deployment can be done in the Azure portal, with Azure CLI, or
            with PowerShell.
          </Typography>
        </AuthenticationStep>
        <AuthenticationStep badgeNumber={2} title="Launch ARM Template">
          <Typography>
            While logged into your Azure account, you can grant Gremlin access
            below. This will open in Azure.
          </Typography>
          {templateUrl ? (
            <AzureDeploymentButton
              isReadOnly={isReadOnly}
              templateUrl={templateUrl}
            />
          ) : (
            <Typography>
              The deployment template is currently unavailable. Please refresh
              the page or try again later.
            </Typography>
          )}
        </AuthenticationStep>
        <AuthenticationStep badgeNumber={3} title="Paste Subscription ID">
          <Typography>
            After a successful template deployment, copy the Subscription ID and
            enter it in the field below.
          </Typography>
          {children}
        </AuthenticationStep>
      </List>
    </Stack>
  )
}
